"use client";

import { useCallback } from "react";

export type MarkdownCommand =
  | {
      type: "wrap";
      before: string;
      after: string;
    }
  | {
      type: "line_prefix";
      prefix: string;
      /**
       * Extra prefixes that should be treated as "on" for toggle-off behavior.
       * Example: checklist should toggle off `- [ ] ` and `- [x] `.
       */
      toggleOffPrefixes?: string[];
      /**
       * If true and the caret is on a line that already has non-whitespace content
       * before the caret, insert the prefix on a new line (GitHub/GitLab-style).
       */
      insertOnNewLineIfTextBeforeCaret?: boolean;
      /**
       * When true, heading commands will first strip any existing markdown
       * heading prefix (H1-H6) before applying the requested prefix.
       */
      replaceExistingHeadingPrefix?: boolean;
    };

interface UseInsertMarkdownAtSelectionArguments {
  inputId: string;
  onChangeValue: (nextValue: string) => void;
}

/**
 * Inserts (and toggles) markdown syntax at the current selection for the input with `inputId`.
 *
 * - Selected text: wrap/toggle wrap (e.g. `**bold**`).
 * - No selection: insert syntax at the caret and place the caret inside the inserted syntax.
 * - Line prefixes: toggle prefix per-line (e.g. `- `, `1. `, `- [ ] `, `> `, headings).
 *
 * Why this reads from the DOM (instead of taking `value` as an argument):
 * - Selection/caret live on the input element, not in React state.
 * - React updates are async; using the live element value avoids stale selection bugs when users type and
 *   immediately trigger a command.
 *
 * Focus/selection updates are scheduled after `onChangeValue` so the DOM value is in sync.
 * For multi-line prefix commands, selection preservation is best-effort.
 */
export function useInsertMarkdownAtSelection({
  inputId,
  onChangeValue,
}: UseInsertMarkdownAtSelectionArguments) {
  return useCallback(
    (command: MarkdownCommand) => {
      const inputElement = globalThis.document?.getElementById(inputId);
      if (
        !(inputElement instanceof HTMLTextAreaElement) &&
        !(inputElement instanceof HTMLInputElement)
      ) {
        return;
      }

      const currentValue = inputElement.value ?? "";
      const start = inputElement.selectionStart ?? 0;
      const end = inputElement.selectionEnd ?? 0;

      // Defer focus/selection changes until after React has a chance to apply the new value.
      const schedule =
        globalThis.requestAnimationFrame ??
        ((callback: FrameRequestCallback) =>
          globalThis.setTimeout(callback, 0));

      type WrapCommand = Extract<MarkdownCommand, { type: "wrap" }>;
      type LinePrefixCommand = Extract<
        MarkdownCommand,
        { type: "line_prefix" }
      >;

      const applyWrap = (wrapCommand: WrapCommand) => {
        const selectedText = currentValue.slice(start, end);
        const linkUrlPlaceholder = "url";

        // Toggle behavior: if selection is already wrapped, unwrap it.
        if (selectedText) {
          const beforeLength = wrapCommand.before.length;
          const afterLength = wrapCommand.after.length;
          const hasBefore =
            start >= beforeLength &&
            currentValue.slice(start - beforeLength, start) ===
              wrapCommand.before;
          const hasAfter =
            end + afterLength <= currentValue.length &&
            currentValue.slice(end, end + afterLength) === wrapCommand.after;

          if (hasBefore && hasAfter) {
            const nextValue =
              currentValue.slice(0, start - beforeLength) +
              selectedText +
              currentValue.slice(end + afterLength);

            onChangeValue(nextValue);
            schedule(() => {
              inputElement.focus();
              inputElement.setSelectionRange(
                start - beforeLength,
                end - beforeLength,
              );
            });
            return;
          }
        }

        // No selection: toggle off if the caret is currently between the syntax;
        // otherwise insert only the syntax and place the caret in the middle.
        if (!selectedText) {
          const beforeLength = wrapCommand.before.length;
          const afterLength = wrapCommand.after.length;

          const hasBeforeAtCaret =
            start >= beforeLength &&
            currentValue.slice(start - beforeLength, start) ===
              wrapCommand.before;
          const hasAfterAtCaret =
            start + afterLength <= currentValue.length &&
            currentValue.slice(start, start + afterLength) ===
              wrapCommand.after;

          if (hasBeforeAtCaret && hasAfterAtCaret) {
            const nextValue =
              currentValue.slice(0, start - beforeLength) +
              currentValue.slice(start + afterLength);

            onChangeValue(nextValue);
            schedule(() => {
              inputElement.focus();
              const caret = start - beforeLength;
              inputElement.setSelectionRange(caret, caret);
            });
            return;
          }

          const nextValue =
            currentValue.slice(0, start) +
            wrapCommand.before +
            wrapCommand.after +
            currentValue.slice(end);

          onChangeValue(nextValue);
          schedule(() => {
            inputElement.focus();

            // Special-case links: `[](url)` should select the `url` placeholder
            // so users can immediately paste/replace it.
            if (wrapCommand.before === "[" && wrapCommand.after === "](url)") {
              const urlStart = start + 3; // `[](` -> url
              const urlEnd = urlStart + linkUrlPlaceholder.length;
              inputElement.setSelectionRange(urlStart, urlEnd);
              return;
            }

            const caret = start + wrapCommand.before.length;
            inputElement.setSelectionRange(caret, caret);
          });
          return;
        }

        const nextValue =
          currentValue.slice(0, start) +
          wrapCommand.before +
          selectedText +
          wrapCommand.after +
          currentValue.slice(end);

        onChangeValue(nextValue);
        schedule(() => {
          inputElement.focus();
          // Special-case links: after creating `[selected](url)`, select the `url` placeholder
          // so users can immediately paste/replace it.
          if (wrapCommand.before === "[" && wrapCommand.after === "](url)") {
            const urlStart = start + 1 + selectedText.length + 2; // `[${text}](` -> url
            const urlEnd = urlStart + linkUrlPlaceholder.length;
            inputElement.setSelectionRange(urlStart, urlEnd);
            return;
          }

          const nextSelectionStart = start + wrapCommand.before.length;
          const nextSelectionEnd = nextSelectionStart + selectedText.length;
          inputElement.setSelectionRange(nextSelectionStart, nextSelectionEnd);
        });
      };

      const applyLinePrefix = (linePrefixCommand: LinePrefixCommand) => {
        // No selection: insert prefix at the caret position.
        if (start === end) {
          const prefix = linePrefixCommand.prefix;
          const toggleOffPrefixes = linePrefixCommand.toggleOffPrefixes ?? [];
          const allPrefixesToRemove = [prefix, ...toggleOffPrefixes];

          const matchedPrefix = allPrefixesToRemove.find((p) => {
            const prefixLength = p.length;
            return (
              start >= prefixLength &&
              currentValue.slice(start - prefixLength, start) === p
            );
          });

          if (matchedPrefix) {
            const prefixLength = matchedPrefix.length;
            const nextValue =
              currentValue.slice(0, start - prefixLength) +
              currentValue.slice(end);

            onChangeValue(nextValue);
            schedule(() => {
              inputElement.focus();
              const caret = start - prefixLength;
              inputElement.setSelectionRange(caret, caret);
            });
            return;
          }

          if (linePrefixCommand.insertOnNewLineIfTextBeforeCaret) {
            const lineStart = currentValue.lastIndexOf("\n", start - 1) + 1;
            const beforeCaretOnLine = currentValue.slice(lineStart, start);
            if (beforeCaretOnLine.trim().length > 0) {
              const indentMatch = beforeCaretOnLine.match(/^(\s*)/);
              const indent = indentMatch?.[1] ?? "";
              const nextValue =
                currentValue.slice(0, start) +
                "\n" +
                indent +
                prefix +
                currentValue.slice(end);

              onChangeValue(nextValue);
              schedule(() => {
                inputElement.focus();
                const caret = start + 1 + indent.length + prefix.length;
                inputElement.setSelectionRange(caret, caret);
              });
              return;
            }
          }

          const nextValue =
            currentValue.slice(0, start) + prefix + currentValue.slice(end);

          onChangeValue(nextValue);
          schedule(() => {
            inputElement.focus();
            const caret = start + prefix.length;
            inputElement.setSelectionRange(caret, caret);
          });
          return;
        }

        // Include all selected lines.
        const endIndexForLine =
          end > 0 && currentValue[end - 1] === "\n" ? end - 1 : end;
        const firstLineStart = currentValue.lastIndexOf("\n", start - 1) + 1;
        const lastLineEndRaw = currentValue.indexOf("\n", endIndexForLine);
        const lastLineEnd =
          lastLineEndRaw === -1 ? currentValue.length : lastLineEndRaw;

        const segment = currentValue.slice(firstLineStart, lastLineEnd);
        const lines = segment.split("\n");

        const prefix = linePrefixCommand.prefix;
        const normalizedPrefix = prefix.trimEnd();
        const headingPrefixRegex = /^#{1,6}\s*/;
        // Treat any `\d+. <whitespace>` prefix as a numbered list marker so we can "swap" list
        // types and toggle numbered lists off even if the user typed multiple spaces/tabs.
        const numberedListPrefixRegex = /^\d+\.\s+/;

        const isBulletedListPrefix = prefix === "- ";
        const isNumberedListPrefix = prefix === "1. ";
        const isChecklistListPrefix = prefix === "- [ ] ";
        const isListPrefixCommand =
          isBulletedListPrefix || isNumberedListPrefix || isChecklistListPrefix;

        const stripOtherListPrefixes = (content: string) => {
          // Order matters: checklist is also prefixed with `- `.
          if (
            content.startsWith("- [ ] ") ||
            content.startsWith("- [x] ") ||
            content.startsWith("- [X] ")
          ) {
            return content.slice("- [ ] ".length);
          }

          const numberedMatch = content.match(numberedListPrefixRegex);
          if (numberedMatch) {
            return content.slice(numberedMatch[0].length);
          }

          if (content.startsWith("- ")) {
            return content.slice("- ".length);
          }

          return content;
        };

        const nextLines = lines.map((line) => {
          if (line.trim().length === 0) {
            return line;
          }

          const leadingWhitespaceMatch = line.match(/^(\s*)/);
          const leadingWhitespace = leadingWhitespaceMatch?.[1] ?? "";
          const content = line.slice(leadingWhitespace.length);

          if (linePrefixCommand.replaceExistingHeadingPrefix) {
            const strippedContent = content.replace(headingPrefixRegex, "");
            const existingHeadingMatch = content.match(/^(#{1,6})\s*/);
            const existingHeadingHashes = existingHeadingMatch?.[1];
            const shouldToggleOff =
              existingHeadingHashes !== undefined &&
              existingHeadingHashes.length === normalizedPrefix.length;

            return (
              leadingWhitespace +
              (shouldToggleOff ? strippedContent : prefix + strippedContent)
            );
          }

          const toggleOffPrefixes = linePrefixCommand.toggleOffPrefixes ?? [];
          const allPrefixesToRemove = [prefix, ...toggleOffPrefixes];

          // List prefixes are mutually exclusive: when applying a list type, strip any other list
          // prefix first so we "swap" instead of stacking (e.g. `1. ` -> `- `, not `- 1. `).
          //
          // For numbered lists, treat any `\d+. ` prefix as "on" for toggle-off.
          const isChecklistLine =
            content.startsWith("- [ ] ") ||
            content.startsWith("- [x] ") ||
            content.startsWith("- [X] ");

          const matchedPrefix = isNumberedListPrefix
            ? content.match(numberedListPrefixRegex)?.[0]
            : isBulletedListPrefix && isChecklistLine
              ? undefined
              : allPrefixesToRemove.find((p) => content.startsWith(p));

          if (matchedPrefix) {
            return leadingWhitespace + content.slice(matchedPrefix.length);
          }

          const nextContent = isListPrefixCommand
            ? stripOtherListPrefixes(content)
            : content;

          return (
            leadingWhitespace +
            (nextContent.startsWith(prefix)
              ? nextContent
              : prefix + nextContent)
          );
        });

        const nextSegment = nextLines.join("\n");
        const nextValue =
          currentValue.slice(0, firstLineStart) +
          nextSegment +
          currentValue.slice(lastLineEnd);

        // Best-effort selection preservation via per-line deltas:
        // we track how much each line grew/shrank after toggling prefixes and apply that offset
        // to the original selection indices.
        const deltas: { lineStartIndex: number; delta: number }[] = [];
        let cursor = firstLineStart;
        for (const [index, oldLine] of lines.entries()) {
          const newLine = nextLines[index];
          deltas.push({
            lineStartIndex: cursor,
            delta: newLine.length - oldLine.length,
          });
          cursor += oldLine.length + 1; // +1 for '\n' (safe for last; cursor isn't used after loop)
        }

        const deltaForIndex = (index: number) =>
          deltas.reduce(
            (sum, { lineStartIndex, delta }) =>
              index >= lineStartIndex ? sum + delta : sum,
            0,
          );

        const nextSelectionStart = Math.max(0, start + deltaForIndex(start));
        const nextSelectionEnd = Math.max(0, end + deltaForIndex(end));

        onChangeValue(nextValue);
        schedule(() => {
          inputElement.focus();
          inputElement.setSelectionRange(nextSelectionStart, nextSelectionEnd);
        });
      };

      if (command.type === "wrap") {
        applyWrap(command);
        return;
      }

      applyLinePrefix(command);
    },
    [inputId, onChangeValue],
  );
}
